/* eslint-disable react/jsx-no-target-blank */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowCircleRightIcon } from '@fronton/icons-react';
import { LinkButton, IconButton } from 'fronton-react';
import { getIsOnlineOnly, getStocksInStores } from 'features/goods-supply';
import { isWebSellable } from 'features/product-info';
import { cx } from 'linaria';
import { useTranslation } from 'react-i18next';
import { analyticsUtils, cn } from 'utils';
import { Carousel } from 'common/components/carousel';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { useWindowSize } from '@navi-toolbox/hooks';
import useInView from 'react-cool-inview';
import { Skeleton } from 'fronton-react/unstable';
import { getHideServices } from 'features/page-model';
import { areRelatedServicesLoading, getRelatedServices } from '../ducks/selectors';
import { RELATED_SERVICE_ITEM_CLICK, RELATED_SERVICE_ITEM_IN_VIEWPORT } from '../ducks';
import type { ViewsModels } from '../types';
import {
  root,
  link,
  linkList,
  linkRow,
  carousel,
  carouselTablet,
  header,
  headerTopBorder,
  carouselContainer,
  moreTwoLinkList,
  carouselContainerManyItems,
  titleSkeleton,
  buttonSkeleton,
  SkeletonTitleRow,
  SkeletonRows,
  ServicesSkeletonRow,
  serviceSkeleton,
} from './styles';
import { RelatedService } from './related-service';
import { RelatedServiceCarouselItem } from './related-service-carousel-item';

export const DATA_QA_RELATED_SERVICES_BUTTON = 'related-services-button-uslugi_mf-pdp';
export const DATA_QA_RELATED_SERVICES_ICON = 'related-services-icon-uslugi_mf-pdp';

const clickHandler = () => {
  analyticsUtils.sendEvent('GA:clickButton', {
    elementBlock: 'productDetail / services',
    elementName: 'toUslugi',
    elementText: 'Помощь мастера',
    elementType: 'button',
  });
};

export function RelatedServices() {
  const { ref, inView } = useInView();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const { t } = useTranslation();

  useEffect(() => {
    if (inView) {
      dispatch(RELATED_SERVICE_ITEM_IN_VIEWPORT());
    }
  }, [dispatch, inView]);

  const stocks = useSelector(getStocksInStores);
  const isSellable = useSelector(isWebSellable);
  const isOnlineOnly = useSelector(getIsOnlineOnly);
  const isHideServices = useSelector(getHideServices);

  const hasEmptyStocksRecords = !stocks.some((stock) => stock.stockValue > 0);
  const isRenderTopBorder = isSellable && (isOnlineOnly || hasEmptyStocksRecords);

  let data: ViewsModels.RelatedService[] = useSelector(getRelatedServices);
  let isLoading = useSelector(areRelatedServicesLoading);

  const service: ViewsModels.RelatedService | null = data?.length === 1 ? data[0] : null;

  const { mobile, tablet } = useBreakpoints() || { tablet: false };

  function onRelatedServiceClick(payload: ViewsModels.RelatedService) {
    dispatch(RELATED_SERVICE_ITEM_CLICK(payload));
  }

  const isColumnsTablet = tablet && data.length > 2;

  useEffect(() => {
    if (data?.length > 2) {
      const carouselEl = document.querySelector('[data-qa="related-services-carousel"]');
      const columns = carouselEl?.querySelectorAll('a');
      const firstColumn = carouselEl?.querySelectorAll('a')[0];

      if (firstColumn && columns) {
        columns.forEach((column, index) => {
          if (index) {
            column.style.width = `${firstColumn.clientWidth}px`;
          }
        });
      }
    }
  }, [data.length, mobile, tablet, ref?.current?.clientWidth, width]);

  if (!data.length) {
    return null;
  }

  const isManyItems = data?.length > 2;

  return isHideServices?.on ? null : (
    <div
      ref={ref as React.LegacyRef<HTMLDivElement> | undefined}
      className={root}
      data-qa="related-services_mf-pdp"
    >
      {isLoading ? (
        <SkeletonRows>
          <SkeletonTitleRow>
            <Skeleton className={titleSkeleton} />
            <Skeleton className={buttonSkeleton} />
          </SkeletonTitleRow>
          <ServicesSkeletonRow>
            <Skeleton className={serviceSkeleton} />
            <Skeleton className={serviceSkeleton} />
          </ServicesSkeletonRow>
        </SkeletonRows>
      ) : (
        <>
          <div
            className={cn({
              [header]: true,
              [headerTopBorder]: isRenderTopBorder,
            })}
          >
            <LinkButton
              href="/uslugi/"
              target="_blank"
              data-qa={DATA_QA_RELATED_SERVICES_BUTTON}
              size="s"
              onClick={clickHandler}
              variant="accent"
            >
              {t('related-services.button')}
            </LinkButton>
            <IconButton
              href="/uslugi/"
              target="_blank"
              data-qa={DATA_QA_RELATED_SERVICES_ICON}
              label={t('related-services.button')}
              onClick={clickHandler}
              size="l"
            >
              <ArrowCircleRightIcon type="outline" size="l" color="text-primary" />
            </IconButton>
          </div>
          {service ? (
            <a
              data-qa="related-services-link_mf-pdp"
              onClick={() => onRelatedServiceClick({ ...service, index: 0 })}
              key={service.title}
              href={service.serviceHref}
              className={cx(link, linkRow)}
              target="_blank"
              rel="noopener"
            >
              <RelatedService service={service} />
            </a>
          ) : (
            <Carousel
              className={cn({ [carousel]: true, [carouselTablet]: isColumnsTablet })}
              containerClassName={isManyItems ? carouselContainerManyItems : carouselContainer}
              data-qa="related-services-carousel"
            >
              {data?.map((item, index) => {
                return (
                  <a
                    data-qa={`related-services-link-${index}_mf-pdp`}
                    onClick={() => onRelatedServiceClick({ ...item, index })}
                    key={item.title}
                    href={item.serviceHref}
                    className={cn({
                      [link]: true,
                      [linkList]: true,
                      [moreTwoLinkList]: isManyItems,
                    })}
                    target="_blank"
                    rel="noopener"
                  >
                    <RelatedServiceCarouselItem item={item} index={index} />
                  </a>
                );
              })}
            </Carousel>
          )}
        </>
      )}
    </div>
  );
}
